import { Component } from "@theme/component";
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
} from "@theme/utilities";
import {
  morphSection,
  replaceSection,
  sectionRenderer,
} from "@theme/section-renderer";
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from "@theme/events";
import { cartPerformance } from "@theme/performance";
import { fetchCart } from "@theme/cart-state";

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /**
   * Finds a cart item row by line item key or line index.
   * @param {string|number} keyOrLine - A line item key (string) or line index (number).
   * @returns {HTMLElement|undefined}
   */
  #findRow(keyOrLine) {
    const isKey = typeof keyOrLine === "string" && isNaN(Number(keyOrLine));
    return isKey
      ? this.refs.cartItemRows.find((row) => row.dataset.key === keyOrLine)
      : this.refs.cartItemRows.find(
          (row) => row.dataset.lineIndex === String(keyOrLine),
        );
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(
      ThemeEvents.discountUpdate,
      this.handleDiscountUpdate,
    );
    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      ThemeEvents.cartUpdate,
      this.#handleCartUpdate,
    );
    document.removeEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange,
    );
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: "change",
    });
    const lineItemRow = this.#findRow(line);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (
      lineItemRow.querySelector("text-component")
    );
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {string|number} lineOrKey - The line item key (string) or line index (number).
   */
  onLineItemRemove(lineOrKey) {
    // If called with a line item key (string from on:click), use id-based removal
    const isKey = typeof lineOrKey === "string" && isNaN(Number(lineOrKey));

    // Capture (before removal) whether this line's free sample should move to a
    // surviving size of the same product (PER-88).
    const reattach = this.#planSampleReattach(lineOrKey);

    const removal = this.updateQuantity({
      ...(isKey ? { id: lineOrKey } : { line: lineOrKey }),
      quantity: 0,
      action: "clear",
    });

    if (reattach) {
      Promise.resolve(removal).then(() => this.#reattachSample(reattach));
    }

    const cartItemRowToRemove = this.#findRow(lineOrKey);

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter(
        (row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key,
      ),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty("--row-height", `${row.clientHeight}px`);
      row.classList.add("removing");

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Before a line is removed, work out whether its attached free sample
   * should move to another size of the same product that stays in the cart
   * (PER-88). Reads the current rows synchronously, before any mutation.
   *
   * @param {string|number} lineOrKey - The line item key or index being removed.
   * @returns {{ sampleVariantId: string, parentLineKey: string, parentVariantId: string } | null}
   */
  #planSampleReattach(lineOrKey) {
    const rows = this.refs.cartItemRows;
    if (!rows) return null;

    const targetRow = this.#findRow(lineOrKey);
    // Only react when removing a top-level product line (not a sample/nested row).
    if (
      !targetRow ||
      "sample" in targetRow.dataset ||
      targetRow.dataset.parentKey
    ) {
      return null;
    }

    const productId = targetRow.dataset.productId;
    if (!productId) return null;

    // The sample nested under the line being removed.
    const sampleRow = rows.find(
      (row) =>
        "sample" in row.dataset &&
        row.dataset.parentKey === targetRow.dataset.key,
    );
    if (!sampleRow?.dataset.variantId) return null;

    // A surviving top-level line for the same product to re-host the sample.
    const sibling = rows.find(
      (row) =>
        row !== targetRow &&
        !("sample" in row.dataset) &&
        !row.dataset.parentKey &&
        !row.classList.contains("removing") &&
        row.dataset.productId === productId,
    );
    if (!sibling?.dataset.key) return null;

    return {
      sampleVariantId: sampleRow.dataset.variantId,
      parentLineKey: sibling.dataset.key,
      parentVariantId: sibling.dataset.variantId,
    };
  }

  /**
   * Re-adds the free sample under a surviving variant after the original
   * parent line (and its nested sample) were removed. Uses parent_line_key
   * to nest under the already-in-cart sibling line.
   *
   * @param {{ sampleVariantId: string, parentLineKey: string, parentVariantId: string }} plan
   */
  async #reattachSample({ sampleVariantId, parentLineKey, parentVariantId }) {
    // Re-check the live cart before re-adding: the removal may have failed
    // (leaving the original sample in place), or cleared the cart, or the
    // surviving parent line may be gone. Only re-attach when the parent line
    // is still present and no nested sample of this variant already exists —
    // otherwise we'd create a duplicate or a sample with no parent.
    let cart;
    try {
      cart = await fetchCart();
    } catch (error) {
      console.error(error);
      return;
    }
    const parentPresent = cart.items.some((item) => item.key === parentLineKey);
    const sampleAlreadyNested = cart.items.some(
      (item) =>
        item.id === Number(sampleVariantId) && item.properties?._nested_sample,
    );
    if (!parentPresent || sampleAlreadyNested) return;

    const sectionIds = [...document.querySelectorAll("cart-items-component")]
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      .filter(Boolean);

    const body = JSON.stringify({
      items: [
        {
          id: Number(sampleVariantId),
          quantity: 1,
          parent_line_key: parentLineKey,
          properties: {
            _nested_sample: "true",
            _parent_variant_id: String(parentVariantId),
          },
        },
      ],
      sections: sectionIds.join(","),
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig("json", { body }),
      );
      const data = await response.json();
      if (data.status || data.errors) {
        console.error(
          "Sample re-attach failed:",
          data.description || data.errors,
        );
        return;
      }

      this.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          source: "cart-items-component",
          sections: data.sections,
        }),
      );

      if (data.sections?.[this.sectionId]) {
        morphSection(this.sectionId, data.sections[this.sectionId]);
      }
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(
      `${config.action}:user-action`,
    );

    this.#disableCartItems();

    const { line, id, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll(
      "cart-items-component",
    );
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    // If removing an item and only sample rows would remain, Shopify will
    // reject the change (422 "paid product"). Clear the cart upfront instead.
    const targetRow = this.#findRow(id || line);

    if (quantity === 0) {
      const paidRows =
        this.refs.cartItemRows?.filter(
          (row) =>
            !("sample" in row.dataset) && !row.classList.contains("removing"),
        ) ?? [];
      if (targetRow && paidRows.includes(targetRow) && paidRows.length === 1) {
        return this.#clearCart(sectionsToUpdate, cartPerformaceUpdateMarker);
      }
    }

    // Check for nested children that need quantity syncing
    const childRows =
      targetRow && quantity > 0
        ? this.refs.cartItemRows.filter(
            (row) =>
              row.dataset.parentKey &&
              row.dataset.parentKey === targetRow.dataset.key,
          )
        : [];

    let url;
    let body;

    if (childRows.length > 0) {
      // Use /cart/update.js to sync parent + children quantities in one request
      const updates = {};
      updates[targetRow.dataset.key] = quantity;
      childRows.forEach((row) => {
        // Complimentary samples stay at 1 regardless of parent quantity
        updates[row.dataset.key] =
          "sample" in row.dataset ? Math.min(quantity, 1) : quantity;
      });
      url = "/cart/update.js";
      body = JSON.stringify({
        updates,
        sections: Array.from(sectionsToUpdate).join(","),
        sections_url: window.location.pathname,
      });
    } else {
      url = Theme.routes.cart_change_url;
      body = JSON.stringify({
        ...(id ? { id } : { line }),
        quantity: quantity,
        sections: Array.from(sectionsToUpdate).join(","),
        sections_url: window.location.pathname,
      });
    }

    cartTotal?.shimmer();

    return fetch(url, fetchConfig("json", { body }))
      .then((response) => {
        return response
          .text()
          .then((text) => ({ status: response.status, text }));
      })
      .then(({ status, text }) => {
        const parsedResponseText = JSON.parse(text);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          // Shopify won't allow a cart with only free items. If removing
          // the last paid item triggers this, clear the entire cart instead.
          if (quantity === 0 && status === 422) {
            return this.#clearCart(
              sectionsToUpdate,
              cartPerformaceUpdateMarker,
            );
          }
          this.#handleCartError(line || id, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          "text/html",
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector(
          '[ref="cartItemCount"]',
        )?.textContent;
        const newCartItemCount = newCartHiddenItemCount
          ? parseInt(newCartHiddenItemCount, 10)
          : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: "cart-items-component",
            sections: parsedResponseText.sections,
          }),
        );

        // Remove rows still in their removal animation before morphing so the
        // morph doesn't reassign the animating element to a surviving row.
        this.querySelectorAll(".removing").forEach((row) => row.remove());

        morphSection(
          this.sectionId,
          parsedResponseText.sections[this.sectionId],
        );

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Clears the entire cart. Used when Shopify rejects removing the last paid
   * item because only free items would remain.
   * @param {Set<string>} sectionsToUpdate - Section IDs to re-render.
   * @param {PerformanceMark} [performanceMarker] - Optional marker to measure.
   */
  async #clearCart(sectionsToUpdate, performanceMarker) {
    try {
      const body = JSON.stringify({
        sections: Array.from(sectionsToUpdate).join(","),
        sections_url: window.location.pathname,
      });

      const response = await fetch(
        "/cart/clear.js",
        fetchConfig("json", { body }),
      );
      const data = await response.json();

      this.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount: 0,
          source: "cart-items-component",
          sections: data.sections,
        }),
      );

      if (data.sections?.[this.sectionId]) {
        // Close the drawer first so DialogComponent can release the body
        // scroll lock; replaceSection swaps the <dialog> element out, which
        // would otherwise strand `position: fixed` on the body.
        const cartDrawer = this.closest("cart-drawer-component");
        if (cartDrawer?.refs?.dialog?.open) {
          await cartDrawer.closeDialog();
        }

        // Full DOM replacement instead of morphing — the empty ↔ non-empty
        // cart transition changes the tree too drastically for morph to handle.
        replaceSection(this.sectionId, data.sections[this.sectionId]);
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.#enableCartItems();
      if (performanceMarker) {
        cartPerformance.measureFromMarker(performanceMarker);
      }
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (lineOrKey, parsedResponseText) => {
    const cartItemRow = this.#findRow(lineOrKey);
    const quantityInput = cartItemRow?.querySelector(
      "input[data-cart-quantity]",
    );

    if (!quantityInput) return;

    quantityInput.value = quantityInput.defaultValue;

    // Find error refs via the row's data-line-index (used for ref naming)
    const line = cartItemRow?.dataset.lineIndex;
    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement))
      throw new Error("Cart item error not found");
    if (!(cartItemErrorContainer instanceof HTMLElement))
      throw new Error("Cart item error container not found");

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove("hidden");
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add("cart-items-disabled");
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove("cart-items-disabled");
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`,
      );

      for (const selector of selectors) {
        const input = selector.querySelector("input[data-cart-quantity]");
        if (!input) continue;

        input.setAttribute("data-cart-quantity", item.quantity.toString());

        // Update the quantity selector's internal state
        if (
          "updateCartQuantity" in selector &&
          typeof selector.updateCartQuantity === "function"
        ) {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    const cartQuantitySelectors = document.querySelectorAll(
      "cart-quantity-selector-component",
    );
    for (const selector of cartQuantitySelectors) {
      if (
        "updateButtonStates" in selector &&
        typeof selector.updateButtonStates === "function"
      ) {
        selector.updateButtonStates();
      }
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error("Section id missing");

    return sectionId;
  }
}

if (!customElements.get("cart-items-component")) {
  customElements.define("cart-items-component", CartItemsComponent);
}
